import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AddCircleIcon from '@mui/icons-material/AddCircle';

import { loadDocumentTypes } from '../../../store/directusService';
import {
    loadParticipantPrivacy,
    updateParticipantPrivacy
} from '../../../store/participantService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { stableSort } from '../../../utils/sortFunctions';
import { uuid } from '../../../utils/stringUtils';
import { hasRole, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import PrivacyTableRow from './PrivacyTableRow';
import { validate } from './validateParticipantPrivacy';

import local from './participantPrivacy.module.css';

const headCells = [
    { id: 'documentType', numeric: false, label: 'Document Type', sortable: false },
    { id: 'active', numeric: false, label: 'Active', sortable: false },
    { id: 'documentRequired', numeric: false, label: 'Document Required', sortable: false },
    { id: 'dateAdded', numeric: false, label: 'Date Added' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'dateAdded',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const ParticipantPrivacyAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const acceptedRoles = [SUPERUSER];
    const submitMessage = 'Participant Privacy has been updated';
    const initialRow = {
        id: '',
        dateAdded: '',
        active: false,
        documentTypeId: '',
        documentRequired: false
    };
    const [rows, setRows] = useState([]);
    const [validDocumentTypes, setValidDocumentTypes] = useState([]);
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [errors, setErrors] = useState({});
    const [isEditDisabled, setIsEditDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const participantPrivacy = useSelector(
        (state) => state.entities.participantService.participantPrivacy
    );
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        if (documentTypes.length < 1) dispatch(loadDocumentTypes());
    }, []);

    useEffect(() => {
        if (!contractId) return;
        setRows([]);
        setErrors({});
        setValidDocumentTypes([]);
        setIsAddButtonDisabled(!hasRole(acceptedRoles, roles));
        setIsUpdateButtonDisabled(!hasRole(acceptedRoles, roles));
        setIsEditDisabled(false);
        dispatch(loadParticipantPrivacy(contractId));
    }, [contractId]);

    useEffect(() => {
        if (!participantPrivacy || documentTypes?.length < 1) return;
        configDocTypes(participantPrivacy);
        setRows(participantPrivacy.map((el) => ({ ...el, initialActiveState: el.active })));
    }, [participantPrivacy, documentTypes]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === submitMessage || errorMessage) {
            setIsUpdateButtonDisabled(false);
            setIsAddButtonDisabled(false);
            setIsEditDisabled(false);
        }
    }, [successMessage, errorMessage]);

    // HELPER fNS
    const configDocTypes = (arr) => {
        const usedDocumentTypes = arr.filter((el) => el.active).map((el) => el.documentTypeId);
        setValidDocumentTypes(
            usedDocumentTypes.length > 0
                ? documentTypes.filter((el) => !usedDocumentTypes.includes(el.id))
                : documentTypes
        );
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <PrivacyTableRow
                key={el.id}
                row={el}
                validDocumentTypes={validDocumentTypes}
                onEditRow={onEditRow}
                onEditDocumentType={onEditDocumentType}
                onEditActive={onEditActive}
                errors={errors}
                isEditDisabled={isEditDisabled}
            />
        ));

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((errors) => ({ ...errors, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onAddPrivacy = () => {
        const newRow = {
            ...initialRow,
            id: uuid(),
            newRow: true,
            dateAdded: new Date().toISOString().slice(0, 10)
        };
        setRows((prev) => [newRow, ...prev]);
    };

    const onEditRow = (row) => {
        setRows(rows.map((el) => (el.id === row.id ? row : el)));
    };

    const onEditActive = (row) => {
        let updatedRows = rows.map((el) => (el.id === row.id ? row : el));
        if (row.active && rows.some((el) => el.newRow)) {
            const activeRows = rows.filter((el) => el.active);
            const rowWithDuplicateDocumentType = activeRows.find(
                (el) => el.documentTypeId === row.documentTypeId
            );
            if (rowWithDuplicateDocumentType) {
                updatedRows = row.newRow
                    ? rows.map((el) => (el.id === row.id ? { ...row, documentTypeId: '' } : el))
                    : rows.map((el) =>
                          el.id === row.id
                              ? row
                              : el.id === rowWithDuplicateDocumentType.id
                                ? { ...rowWithDuplicateDocumentType, documentTypeId: '' }
                                : el
                      );
            }
        }
        setRows(updatedRows);
        configDocTypes(updatedRows);
    };

    const onEditDocumentType = (row) => {
        clearError(`documentTypeId-${row.id}`);
        const updatedRows = rows.map((el) => (el.id === row.id ? row : el));
        setRows(updatedRows);
        configDocTypes(updatedRows);
        onEditRow(row);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const updatedRows = rows.map((el) => ({
            id: el.newRow ? null : el.id,
            documentTypeId: el.documentTypeId,
            active: el.active,
            documentRequired: el.documentRequired
        }));
        const newErrors = validate(rows, updatedRows, participantPrivacy);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        const payload = { contractId, entries: updatedRows };
        dispatch(updateParticipantPrivacy(payload, submitMessage));
    };

    // AWAITING CONTENT
    if (!contractId) return <LoadingSpinner content={NO_CONTRACT_ERROR} />;

    // RENDER
    return (
        <div className={local.participantPrivacyWrapper}>
            {rows.length < 1 ? (
                <LoadingSpinner content="No privacy rows found" />
            ) : (
                <div className={local.tableWrapper}>
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        loadResults={loadParticipantPrivacy}
                        passRowMetaDataUp={setRowMetaData}
                        apiParams={contractId}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                </div>
            )}
            <button
                data-testid="add-privacy-button"
                type="button"
                className={`${local.textAddButton}`}
                disabled={isAddButtonDisabled}
                onClick={onAddPrivacy}>
                <AddCircleIcon />
                Add New Participant Privacy
            </button>
            {rows.length > 0 && (
                <Button
                    id="updateParticipantConsentButton"
                    disabled={isUpdateButtonDisabled}
                    content="UPDATE PARTICIPANT PRIVACY"
                    error={errors.button}
                    clearError={() => clearError('button')}
                    onClick={onSubmit}
                />
            )}
        </div>
    );
};

ParticipantPrivacyAdmin.propTypes = {
    contractId: PropTypes.string
};

export default ParticipantPrivacyAdmin;
